import AccountService from "../services/AccountService.js";
import AccountSync from "../services/AccountSync.js";
import { authError, forgotError } from "../services/accountErrors.js";

/**
 * Pages d'authentification : /connexion, /inscription, /mot-de-passe-oublie.
 *
 * Une seule classe pour les trois — même enveloppe, même gestion d'erreurs, seul
 * l'appel d'API change — pilotée par `data-auth-mode` sur la racine de la page.
 * Elles remplacent la popup de compte : un formulaire de connexion a une URL,
 * un bouton retour et une place dans l'historique, ce qu'une popup n'a pas.
 */
const PROFILE = { fr: "/profil", en: "/en/profile" };

export default class AuthPage {
    constructor() {
        this.page = document.querySelector(".page.auth-page");
        if (!this.page) return;

        this.mode = this.page.dataset.authMode;
        this.form = this.page.querySelector(".auth-form");
        this.t = (k) => window.geoNerdApp.languageManager.t(k);

        this.form?.addEventListener("submit", (e) => this.handleSubmit(e));
        this.keepNextOnSwitchLinks();

        // Déjà connecté : ces pages n'ont plus rien à proposer. On attend de
        // connaître la session (bootstrap async) avant de trancher, sinon on
        // redirigerait tout le monde ou personne selon la vitesse du réseau.
        window.geoNerdApp.accountReady?.then(() => {
            if (AccountSync.isLoggedIn()) window.location.replace(this.url(PROFILE));
        });
    }

    url(table) {
        return table[window.geoNerdApp.languageManager.currentLang] || table.fr;
    }

    /**
     * Où aller une fois connecté : la page d'où vient le joueur (`?next=`, posé
     * par la proposition de compte de fin de partie), sinon le profil. Seul un
     * chemin du site est accepté (`/…`, jamais `//hote` ni une URL complète).
     */
    afterAuthUrl() {
        return this.nextPath() || this.url(PROFILE);
    }

    nextPath() {
        const next = new URLSearchParams(window.location.search).get("next");
        return next && /^\/(?!\/)/.test(next) && !next.includes("\\") ? next : null;
    }

    /** « Tu as déjà un compte ? » garde la page de retour d'une page à l'autre. */
    keepNextOnSwitchLinks() {
        const next = this.nextPath();
        if (!next) return;
        this.page.querySelectorAll(".auth-switch a").forEach(a => {
            const url = new URL(a.getAttribute("href"), window.location.origin);
            url.searchParams.set("next", next);
            a.setAttribute("href", url.pathname + url.search);
        });
    }

    handleSubmit(e) {
        e.preventDefault();
        if (this.mode === "forgot") return this.handleForgot();
        return this.handleAuth();
    }

    async handleAuth() {
        const form = this.form;
        const email = form.querySelector('input[type="email"]').value.trim();
        const password = form.querySelector('input[type="password"]').value;
        const username = this.mode === "register"
            ? (form.querySelector('input[name="username"]')?.value.trim() || undefined)
            : undefined;

        this.setBusy(true);
        const res = this.mode === "register"
            ? await AccountService.register(email, password, username, this.registerExtras())
            : await AccountService.login(email, password, window.geoNerdApp.languageManager.currentLang === "en" ? "en" : "fr");

        if (res.ok && res.data?.user) {
            // Fusionne serveur↔local AVANT de quitter la page : le profil affiché
            // juste après doit montrer la sauvegarde fusionnée, pas la locale.
            await AccountSync.afterAuth(res.data.user);
            window.location.href = this.afterAuthUrl();
            return;
        }
        this.setBusy(false);
        this.showMessage(authError(res, this.mode, this.t), "error");
    }

    /**
     * Langue et fuseau du joueur (pour écrire et envoyer ses rappels à la bonne
     * heure), et son accord éventuel pour les rappels par e-mail.
     */
    registerExtras() {
        let timezone;
        try {
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) { /* fuseau inconnu : le serveur prendra Paris */ }
        return {
            reminderEmail: Boolean(this.form.querySelector('input[name="reminderEmail"]')?.checked),
            lang: window.geoNerdApp.languageManager.currentLang === "en" ? "en" : "fr",
            ...(timezone ? { timezone } : {}),
        };
    }

    async handleForgot() {
        const emailInput = this.form.querySelector('input[type="email"]');
        this.setBusy(true);
        const res = await AccountService.forgotPassword(
            emailInput.value.trim(),
            window.geoNerdApp.languageManager.currentLang
        );
        this.setBusy(false);

        // La route répond 200 même pour un email inconnu, mais elle peut ne jamais
        // être atteinte (réseau, rate-limit, panne) : annoncer « lien envoyé » dans
        // ces cas laisse le joueur attendre un mail qui n'arrivera pas. On garde
        // l'email saisi pour qu'il puisse réessayer sans le retaper.
        if (!res.ok) {
            this.showMessage(forgotError(res, this.t), "error");
            return;
        }
        this.showMessage(this.t("account.forgotSent"), "success");
        this.form.reset();
    }

    setBusy(busy) {
        const btn = this.form.querySelector('button[type="submit"]');
        if (btn) btn.disabled = busy;
        this.form.classList.toggle("is-busy", busy);
    }

    showMessage(msg, kind) {
        const el = this.form.querySelector(".account-message");
        if (!el) return;
        el.textContent = msg;
        el.className = `account-message ${kind}`;
    }
}
